import { KitButton, KitIcon, KitInput, KitText } from '@omni/kit/components';
import LeftNavButton, { LeftNavType } from '@omni/kit/components/LeftNavButton';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import React, { ReactElement, useEffect, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import * as Constants from '../../shared/Constants';
import { setChannel } from '../../shared/redux/actions/ChatActions';
import { createNewChannel } from '../../shared/redux/actions/SystemActions';
import {
  channelListSelector,
  userSelector,
} from '../../shared/redux/selectors';
import { ChannelType } from '../../shared/redux/types';
import { getShortChannelUrl } from '../../utilities/chatUtilities';
import PlatformKeyboardSpacer from '../components/PlatformKeyboardSpacer';

// const debug = require('debug')('tca:chat:screen:CreateChannelModal');

//******************************************************************************
// Component
//******************************************************************************

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function CreateChannelModal({ navigation }): ReactElement {
  const { t } = useTranslation(['messaging']);

  const [channelName, setChannelName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { sizeClass } = useSizeClass();
  const channelList = useSelector(channelListSelector);
  const user = useSelector(userSelector);

  const dispatch = useDispatch();

  const _onButtonPress = () => {
    const isPublic = false;
    setIsLoading(true);
    dispatch(
      createNewChannel(
        ChannelType.Group,
        false,
        isPublic,
        channelName,
        user ? [user] : []
      )
    );
  };

  useEffect(() => {
    if (channelList && channelList[0]?.name === channelName) {
      setChannelName('');
      setIsLoading(false);
      const channel = channelList[0];

      if (Platform.OS === 'web' && sizeClass !== SizeClass.Small) {
        navigation.goBack();
        dispatch(setChannel(channel));
        navigation.navigate(channel.url);

        return;
      }

      navigation.navigate(Constants.CHAT_SCREEN, {
        channel,
        channelName: channel.name,
        channelId: getShortChannelUrl(channel.url),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelList, navigation]);

  useEffect(() => {
    navigation.setOptions({
      title: 'Create group',
      headerLeft: () => (
        <LeftNavButton
          type={LeftNavType.Dismiss}
          title='Create group'
          onPress={navigation.goBack}
        />
      ),
    });
  }, [navigation]);

  return (
    <View style={{ flex: 1 }}>
      {Platform.OS === 'web' && sizeClass !== SizeClass.Small ? (
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <LeftNavButton
            type={LeftNavType.Dismiss}
            title={t('messaging:createGroup')}
            onPress={navigation.goBack}
          />
          <KitText bold black fontSize={20}>
            {t('messaging:createGroup')}
          </KitText>
          <View style={{ opacity: 0 }}>
            <LeftNavButton
              disabled
              type={LeftNavType.Dismiss}
              title={t('messaging:createGroup')}
              onPress={() => null}
            />
          </View>
        </View>
      ) : null}

      <ScrollView style={styles.safeContainer}>
        <View style={styles.viewContainer}>
          <KitInput
            label={t('messaging:groupName')}
            placeholder={t('messaging:groupNameTeamNameTopicEtc')}
            value={channelName}
            onChangeText={setChannelName}
          />
          <View style={styles.descriptionContainer}>
            <View style={styles.descriptionBlock}>
              {Platform.OS === 'web' ? (
                <KitIcon
                  style={{ flexShrink: 0 }}
                  name='user-search'
                  color={Colors.brand}
                  size={21}
                />
              ) : (
                <Image
                  style={styles.descriptionImage}
                  source={require('@omni/kit/img/icon-createchannel-find.png')}
                />
              )}
              <KitText gray fontSize={16} style={styles.descriptionText}>
                {t('messaging:nameYourGroupSomethingThatsEasilyRecognizable')}
              </KitText>
            </View>
            <View style={styles.descriptionBlock}>
              {Platform.OS === 'web' ? (
                <KitIcon
                  style={{ flexShrink: 0 }}
                  name='megaphone'
                  color={Colors.brand}
                  size={21}
                />
              ) : (
                <Image
                  style={styles.descriptionImage}
                  source={require('@omni/kit/img/icon-createchannel-safe.png')}
                />
              )}
              <KitText gray fontSize={16} style={styles.descriptionText}>
                {t('messaging:groupCreatorInstructions')}
              </KitText>
            </View>
          </View>
          <View style={styles.buttonContainer}>
            <KitButton
              disabled={!channelName}
              isLoading={isLoading}
              onPress={_onButtonPress}
              title={t('messaging:createGroup')}
            />
          </View>
        </View>
      </ScrollView>
      <PlatformKeyboardSpacer />
    </View>
  );
}

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  buttonContainer: {
    marginTop: 48,
  },
  viewContainer: {
    flex: 1,
    marginTop: 28,
    marginBottom: Spacing.xl,
    paddingHorizontal: Spacing.xl,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: colorForScheme({ default: Colors.N0 }),
  },
  descriptionContainer: {
    marginTop: Spacing.xl,
  },
  descriptionBlock: {
    flexDirection: 'row',
    flexWrap: Platform.OS === 'web' ? 'nowrap' : 'wrap',
    paddingTop: Spacing.xl,
  },
  descriptionImage: {
    tintColor: colorForScheme({ default: Colors.brand }),
  },
  descriptionText: {
    flex: 1,
    paddingLeft: Spacing.l,
    lineHeight: 24,
  },
});
